export interface DemoModel {
  id: string;
  label: string;
  src: string;
  orientation: string;
  startYaw: number;
  startView: [number, number, number, number, number, number] | null;
  note: string;
  thumb: string;
  bytes: number;
  splats: number;
  hidden: boolean;
  nsfw: boolean;
}

interface CatalogPayload {
  models?: Array<{
    id: string;
    label: string;
    src: string;
    file: string;
    thumb?: string;
    bytes?: number;
    splats?: number;
    hidden?: boolean;
    nsfw?: boolean;
    startYaw?: number;
    startView?: { focus?: number[]; eye?: number[] } | null;
  }>;
}

/** Files that are not the Schorn / Spark 180° X convention. */
const ORIENTATION_OVERRIDES: Record<string, string> = {
  "toy-cat": "0,0,180",
};

function decorate(entry: {
  id: string;
  label: string;
  src: string;
  file: string;
  thumb?: string;
  bytes?: number;
  splats?: number;
  hidden?: boolean;
  nsfw?: boolean;
  startYaw?: number;
  startView?: { focus?: number[]; eye?: number[] } | null;
}): DemoModel {
  const yaw = typeof entry.startYaw === "number" && Number.isFinite(entry.startYaw) ? entry.startYaw : 0;
  const focus = entry.startView?.focus;
  const eye = entry.startView?.eye;
  const startView =
    focus?.length === 3 && eye?.length === 3 && [...focus, ...eye].every((n) => typeof n === "number" && Number.isFinite(n))
      ? ([focus[0], focus[1], focus[2], eye[0], eye[1], eye[2]] as [number, number, number, number, number, number])
      : null;
  return {
    id: entry.id,
    label: entry.label,
    src: entry.src,
    orientation: ORIENTATION_OVERRIDES[entry.id] ?? "180,0,0",
    startYaw: yaw,
    startView,
    note: entry.file,
    thumb: entry.thumb ?? "",
    bytes: entry.bytes ?? 0,
    splats: entry.splats ?? 0,
    hidden: Boolean(entry.hidden),
    nsfw: Boolean(entry.nsfw),
  };
}

export function formatLibraryBytes(bytes: number): string {
  if (!(bytes > 0)) {
    return "";
  }
  if (bytes >= 1024 * 1024) {
    const mb = bytes / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
}

export function formatLibrarySplats(count: number): string {
  if (!(count > 0)) {
    return "";
  }
  if (count >= 1_000_000) {
    const millions = count / 1_000_000;
    return `${millions >= 10 ? Math.round(millions) : millions.toFixed(1)}M splats`;
  }
  if (count >= 1000) {
    const thousands = count / 1000;
    return `${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}k splats`;
  }
  return `${count} splats`;
}

export async function fetchDemoModels(): Promise<DemoModel[]> {
  const response = await fetch(`/models/manifest.json?t=${Date.now()}`);
  if (!response.ok) {
    throw new Error(`Model list failed (${response.status})`);
  }
  const payload = (await response.json()) as CatalogPayload;
  return (payload.models ?? []).map(decorate);
}

export function modelFromQuery(models: DemoModel[]): DemoModel | undefined {
  try {
    const id = new URLSearchParams(window.location.search).get("model");
    if (!id) {
      return undefined;
    }
    return models.find((model) => model.id === id);
  } catch {
    return undefined;
  }
}
